import React from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {AddPicturesIcon, Button, ProgressBar} from '@taopix/taopix-design-system';
import {DeviceUploaderProps} from './DeviceUploader';

type DeviceUploaderStatusPanelProps = DeviceUploaderProps & {
  completed: number;
  total: number;
  onTriggerUpload: () => void;
  onCancel: () => void;
};

const DeviceUploaderStatusPanel = ({
  completed,
  total,
  expired,
  onTriggerUpload,
  onCancel,
}: DeviceUploaderStatusPanelProps) => {
  const {t} = useTranslation(['uploadexternalassets']);

  // status message
  const StatusMessage = ({completed, total}: {completed: number; total: number}) => {
    if (completed < total) {
      return <p>{t('str_MessageExternalUploadStatusUploading', {ns: 'uploadexternalassets'})}</p>;
    }

    if (total === 0) {
      return <p>{t('str_MessageExternalUploadStatusAwaiting', {ns: 'uploadexternalassets'})}</p>;
    }

    if (total === 1) {
      return (
        <p>{t('str_MessageExternalUploadStatusUploadedSingular', {ns: 'uploadexternalassets'})}</p>
      );
    }

    if (total > 1) {
      return (
        <p>
          {t('str_MessageExternalUploadStatusUploadedPlural', {
            ns: 'uploadexternalassets',
          }).replace('^0', String(total))}
        </p>
      );
    }
    return null;
  };

  // progress bar status message
  const ProgressMessage = ({completed, total}: {completed: number; total: number}) => {
    const className = classNames({
      'hidden opacity-0': total === 0 || completed === total,
    });
    return (
      <p className={className}>
        {total === completed
          ? t('str_MessageUploadMorePicturesOrClose', {ns: 'uploadexternalassets'})
          : t('str_MessageExternalUploadProgress', {ns: 'uploadexternalassets'})
              .replace('^0', String(completed))
              .replace('^1', String(total))}
      </p>
    );
  };

  // view buttons
  const ActionButton = ({
    completed,
    total,
    expired,
    onTriggerUpload,
    onCancel,
  }: DeviceUploaderStatusPanelProps) => {
    if (total === 0 || total === completed) {
      // Show the Add Pictures button if nothing is uploading
      const buttonLabel =
        total > 0
          ? `${t('str_ButtonAddMorePictures', {
              ns: '*',
            })}`
          : `${t('str_ButtonAddPictures', {
              ns: '*',
            })}`;

      return (
        <Button
          label={buttonLabel}
          buttonStyle={'primary'}
          startIcon={<AddPicturesIcon />}
          onClick={onTriggerUpload}
          disabled={expired}
        />
      );
    }
    // Show the cancel button while there are pictures uploading
    return (
      <Button
        label={t('str_LabelCancelUpload', {ns: 'uploadexternalassets'})}
        buttonStyle={'negative'}
        onClick={() => onCancel()}
      />
    );
  };

  const progressBarClasses = classNames(
    {'hidden opacity-0': total === 0 || completed === total},
    'w-full',
    'max-w-[300rem]'
  );

  return (
    <>
      <StatusMessage total={total} completed={completed} />
      <ProgressBar
        progress={completed === 0 && total === 0 ? 0 : (completed / total) * 100}
        max={100}
        id="upload-progress-bar"
        className={progressBarClasses}
      />
      <ProgressMessage total={total} completed={completed} />
      <ActionButton
        total={total}
        completed={completed}
        onTriggerUpload={onTriggerUpload}
        onCancel={onCancel}
        expired={expired}
      />
    </>
  );
};

export default DeviceUploaderStatusPanel;
